import React, { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Drawer, Tag, Button, Spin, Typography, Space, Divider, message,
} from 'antd'
import {
  CalendarOutlined, CarOutlined, UserOutlined, FileTextOutlined,
  ArrowRightOutlined, ReloadOutlined, CloseCircleOutlined,
} from '@ant-design/icons'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import itLocale from '@fullcalendar/core/locales/it'
import type { DatesSetArg, EventClickArg, DateClickArg } from '@fullcalendar/core'
import {
  checkOAuthStatus, fetchCalendarEvents,
  cancelAppointment,
  type CalendarEvent,
} from '@/services/calendarService'
import { useWorkOrder } from '@/hooks/useWorkOrders'
import { useWOStatusTypesList } from '@/hooks/useSystemTables'
import { usePermission } from '@/hooks/usePermission'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { useEffect } from 'react'
import './CalendarPage.css'

const { Text, Title } = Typography

// Calcola timeMin/timeMax RFC3339 per il mese corrente
function monthRange(date: Date) {
  const y = date.getFullYear()
  const m = date.getMonth()
  const first = new Date(y, m, 1)
  const last = new Date(y, m + 1, 0, 23, 59, 59)
  const toRFC = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}+01:00`
  }
  return { timeMin: toRFC(first), timeMax: toRFC(last) }
}

interface DrawerWOProps {
  workOrderId: number
  statoColorMap: Record<string, string>
  statoLabelMap: Record<string, string>
  canCancelCalendar: boolean
  onClose: () => void
  onCancelled: () => void
}

const DrawerWOContent: React.FC<DrawerWOProps> = ({
  workOrderId, statoColorMap, statoLabelMap, canCancelCalendar, onClose, onCancelled,
}) => {
  const navigate = useNavigate()
  const { data: wo, isLoading } = useWorkOrder(workOrderId)
  const [cancelling, setCancelling] = useState(false)

  if (isLoading) return <Spin style={{ display: 'block', margin: '40px auto' }} />
  if (!wo) return <Text type="secondary">Scheda non trovata.</Text>

  const vehicleLabel = [wo.vehicle_marca, wo.vehicle_modello].filter(Boolean).join(' ')

  const handleCancelAppointment = async () => {
    try {
      setCancelling(true)
      await cancelAppointment(wo.id)
      message.success('Appuntamento cancellato')
      onCancelled()
      onClose()
    } catch (e: any) {
      message.error(e?.message || 'Errore durante la cancellazione')
    } finally {
      setCancelling(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1 }}>
        {/* Numero scheda */}
        <div style={{ marginBottom: 16 }}>
          <Space align="center">
            <FileTextOutlined style={{ color: '#1890ff', fontSize: 18 }} />
            <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
              {wo.numero_scheda}
            </Title>
            <Tag color={statoColorMap[wo.stato] ?? 'default'} style={{ margin: 0 }}>
              {statoLabelMap[wo.stato] ?? wo.stato}
            </Tag>
          </Space>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* Cliente */}
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            Cliente
          </Text>
          <Space>
            <UserOutlined style={{ color: '#8c8c8c' }} />
            <Text strong>{wo.customer_nome ?? '—'}</Text>
          </Space>
        </div>

        {/* Veicolo */}
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            Veicolo
          </Text>
          <Space>
            <CarOutlined style={{ color: '#8c8c8c' }} />
            <Text>{vehicleLabel || '—'}</Text>
            {wo.vehicle_targa && (
              <Tag color="geekblue" style={{ fontWeight: 600 }}>{wo.vehicle_targa}</Tag>
            )}
          </Space>
        </div>

        {/* Data appuntamento */}
        {wo.data_appuntamento && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Appuntamento
            </Text>
            <Space>
              <CalendarOutlined style={{ color: '#8c8c8c' }} />
              <Text>{new Date(wo.data_appuntamento).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}</Text>
            </Space>
          </div>
        )}
      </div>

      {/* Azioni */}
      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Button
          type="primary"
          icon={<ArrowRightOutlined />}
          block
          onClick={() => { navigate(`/schede-lavoro/${wo.id}`); onClose() }}
        >
          Apri scheda completa
        </Button>
        {canCancelCalendar && wo.google_event_id && (
          <Button
            danger
            icon={<CloseCircleOutlined />}
            block
            loading={cancelling}
            onClick={handleCancelAppointment}
          >
            Cancella appuntamento
          </Button>
        )}
      </div>
    </div>
  )
}

const CalendarPage: React.FC = () => {
  const navigate = useNavigate()
  const { setTitle } = usePageTitle()
  const calendarRef = useRef<FullCalendar>(null)

  const { data: statusTypes = [] } = useWOStatusTypesList()
  const statoColorMap = Object.fromEntries(statusTypes.map(s => [s.nome, s.colore]))
  const statoLabelMap = Object.fromEntries(statusTypes.map(s => [s.nome, s.nome]))
  const canCancelCalendar = usePermission('calendar.create')

  const [dateRange, setDateRange] = useState(() => monthRange(new Date()))
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedWOId, setSelectedWOId] = useState<number | null>(null)
  const [drawerTitle, setDrawerTitle] = useState('Appuntamento')

  useEffect(() => { setTitle('Calendario') }, [setTitle])

  // Stato connessione Google Calendar
  const { data: oauthStatus } = useQuery({
    queryKey: ['oauth-status'],
    queryFn: checkOAuthStatus,
    staleTime: 60_000,
  })
  const calendarConnected = oauthStatus?.configured ?? false

  // Carica eventi del mese corrente
  const { data: rawEvents = [], isLoading, refetch, error: eventsError } = useQuery({
    queryKey: ['calendar-events', dateRange.timeMin, dateRange.timeMax],
    queryFn: () => fetchCalendarEvents(dateRange.timeMin, dateRange.timeMax),
    enabled: calendarConnected,
    refetchInterval: 30_000,
    retry: false,
  })

  // Converti per FullCalendar
  const _today = new Date()
  _today.setHours(0, 0, 0, 0)
  const fcEvents = rawEvents.map((e: CalendarEvent) => {
    const isOverdue = !!e.work_order_id && new Date(e.start) < _today
    return {
      id: e.id,
      title: e.title,
      start: e.start,
      end: e.end,
      extendedProps: { work_order_id: e.work_order_id },
      color: isOverdue ? '#fa8c16' : (e.work_order_id ? '#1890ff' : '#8c8c8c'),
    }
  })

  const handleDatesSet = useCallback((info: DatesSetArg) => {
    setDateRange({ timeMin: info.startStr, timeMax: info.endStr })
  }, [])

  const handleEventClick = useCallback((info: EventClickArg) => {
    const woId = info.event.extendedProps.work_order_id as number | undefined
    if (woId) {
      setSelectedWOId(woId)
      setDrawerTitle(info.event.title)
      setDrawerOpen(true)
    }
  }, [])

  const handleDateClick = useCallback((info: DateClickArg) => {
    navigate(`/schede-lavoro/new?data_appuntamento=${info.dateStr}`)
  }, [navigate])

  const handleDrawerClose = () => {
    setDrawerOpen(false)
    setSelectedWOId(null)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '0 0 16px' }}>
      {/* Alert connessione */}
      {oauthStatus && !calendarConnected && (
        <Alert
          type="warning"
          showIcon
          message="Google Calendar non collegato"
          description="Collega Google Calendar nelle impostazioni per visualizzare gli appuntamenti."
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={() => navigate('/settings')}>
              Vai alle impostazioni
            </Button>
          }
        />
      )}
      {calendarConnected && eventsError && (
        <Alert
          type="error"
          showIcon
          message="Errore caricamento eventi"
          description="Impossibile caricare gli eventi da Google Calendar. Il token potrebbe essere scaduto — vai nelle impostazioni per ricollegare."
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={() => navigate('/settings')}>
              Impostazioni
            </Button>
          }
        />
      )}

      {/* Toolbar ricarica */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Button
          icon={<ReloadOutlined />}
          size="small"
          onClick={() => refetch()}
          loading={isLoading}
          disabled={!calendarConnected}
        >
          Aggiorna
        </Button>
      </div>

      {/* Calendario */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          locale={itLocale}
          headerToolbar={{ left: 'prev,next today', center: 'title', right: '' }}
          events={fcEvents}
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          height="100%"
          dayCellClassNames={() => 'fc-day-clickable'}
        />
      </div>

      {/* Drawer dettaglio scheda */}
      <Drawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        title={<Space><CalendarOutlined />{drawerTitle}</Space>}
        width={360}
        styles={{ body: { display: 'flex', flexDirection: 'column', height: '100%' } }}
      >
        {selectedWOId && (
          <DrawerWOContent
            workOrderId={selectedWOId}
            statoColorMap={statoColorMap}
            statoLabelMap={statoLabelMap}
            canCancelCalendar={canCancelCalendar}
            onClose={handleDrawerClose}
            onCancelled={refetch}
          />
        )}
      </Drawer>
    </div>
  )
}

export default CalendarPage
